import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { Save, CalendarPlus, ArrowLeft, Copy } from "lucide-react";

export default function ContentEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [showSched, setShowSched] = useState(false);
  const [channel, setChannel] = useState("social");
  const [when, setWhen] = useState("");

  useEffect(() => { (async () => {
    const { data } = await api.get(`/content/${id}`);
    setC(data);
  })(); }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/content/${id}`, { title: c.title, body: c.body });
      setC(data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const schedule = async () => {
    if (!when) { toast.error("Pick a date/time"); return; }
    setScheduling(true);
    try {
      await api.post("/schedule", { content_id: id, channel, scheduled_at: new Date(when).toISOString(), notes: "" });
      toast.success("Scheduled");
      setShowSched(false);
      nav("/schedule");
    } catch { toast.error("Schedule failed"); }
    finally { setScheduling(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(c.body);
    toast.success("Copied");
  };

  if (!c) return <AppShell title="Editor"><div className="text-zinc-500">Loading…</div></AppShell>;

  return (
    <AppShell eyebrow={`${c.content_type.replace(/_/g," ")} · ${c.timeframe}`} title="Edit content"
      actions={
        <div className="flex gap-2">
          <button onClick={()=>nav("/content")} data-testid="editor-back" className="border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button onClick={copy} data-testid="editor-copy" className="border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button onClick={()=>setShowSched(true)} data-testid="editor-schedule" className="border border-[#0033FF] text-[#0033FF] px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0033FF] hover:text-white inline-flex items-center gap-2">
            <CalendarPlus className="w-3.5 h-3.5" /> Schedule
          </button>
          <button onClick={save} disabled={saving} data-testid="editor-save" className="bg-[#0033FF] text-white px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center gap-2">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      }>
      <div className="border border-zinc-200">
        <input data-testid="editor-title" className="w-full px-6 py-5 text-2xl font-black tracking-tight border-b border-zinc-200 focus:outline-none"
          value={c.title} onChange={(e)=>setC({...c, title: e.target.value})} />
        <textarea data-testid="editor-body" className="w-full px-6 py-5 min-h-[480px] text-base leading-relaxed focus:outline-none font-normal"
          value={c.body} onChange={(e)=>setC({...c, body: e.target.value})} />
      </div>

      {showSched && (
        <div className="fixed inset-0 bg-zinc-900/60 z-50 flex items-center justify-center p-4" onClick={()=>setShowSched(false)}>
          <div className="bg-white border border-zinc-200 p-6 max-w-md w-full" onClick={(e)=>e.stopPropagation()} data-testid="schedule-modal">
            <div className="text-xs tracking-[0.25em] uppercase text-[#0033FF] font-bold">New drop</div>
            <h3 className="text-2xl font-black tracking-tighter mt-1 mb-5">Schedule this piece</h3>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Channel</label>
            <select data-testid="schedule-channel" value={channel} onChange={(e)=>setChannel(e.target.value)} className="w-full mt-2 mb-4 px-3 py-2.5 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#0033FF]">
              <option value="social">Social media</option>
              <option value="newsletter">Newsletter</option>
              <option value="email">Email</option>
              <option value="presentation">Presentation</option>
              <option value="other">Other</option>
            </select>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">When</label>
            <input data-testid="schedule-when" type="datetime-local" value={when} onChange={(e)=>setWhen(e.target.value)} className="w-full mt-2 mb-5 px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#0033FF]" />
            <div className="flex justify-end gap-2">
              <button onClick={()=>setShowSched(false)} className="border border-zinc-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100">Cancel</button>
              <button onClick={schedule} disabled={scheduling} data-testid="schedule-confirm" className="bg-[#0033FF] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-50">
                {scheduling ? "Scheduling…" : "Schedule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
